import React from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';

import {
  DownloadBottom as DownloadIcon,
  SearchSimilar,
} from '@videoblocks/react-icons';
import { Popper, Placements } from '@videoblocks/storywind';

import { selectMemberBins } from '../../../../../Member/MemberBins/selectors/foldersSelectors';
import {
  selectHasVideoSubscription,
  selectIsLoggedIn,
} from '../../../../../auth/AuthSelectors';
import { default as FavoriteButtonIcon } from '../../../../../common/components/FavoriteButton';
import SignUpForMakerPopover from '../../../../../common/components/Popover/SignUpForMakerPopover';
import {
  convertStockItemContextToOriginArea,
  isStockItemInAnyFolder,
} from '../../../../../common/utils';
import { DrawerType } from '../../../SearchTypes';
import {
  selectDrawerIsOpen,
  selectDrawerSelectedItemId,
  selectDrawerType,
  selectShowAnimatedAiThumbnailButton,
} from '../../../selectors/searchSelectors';
import ArrowIcon from '../../filters/ArrowIcon';
import { VideoStockItemCardProps } from '../VideoStockItemCard';

/**
 * Hook that observes the stock item container width to determine if compact mode should be used.
 * Compact mode shows only icons when the container is smaller than the threshold.
 */
const useStockItemCompact = (threshold = 465) => {
  const [isCompact, setIsCompact] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const debouncedSetIsCompact = useDebouncedCallback((width: number) => {
    setIsCompact(width < threshold);
  }, 100);

  React.useEffect(() => {
    const observeContainer = () => {
      if (!containerRef.current) return;

      // Find the closest stock item container to measure its width
      const stockItemContainer = containerRef.current.closest('.stock-item');
      if (!stockItemContainer) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          debouncedSetIsCompact(width);
        }
      });

      resizeObserver.observe(stockItemContainer);

      // Initial check
      const width = stockItemContainer.getBoundingClientRect().width;
      setIsCompact(width < threshold);

      return () => {
        resizeObserver.disconnect();
      };
    };

    const cleanup = observeContainer();
    return cleanup;
  }, [threshold, debouncedSetIsCompact]);

  return { isCompact, containerRef };
};

// all buttons extracted from VideoStockItemCard
export const DownloadButton = (
  props: Pick<
    VideoStockItemCardProps,
    'shouldShowDownloadButton' | 'downloadButtonTelemetryAction'
  > & {
    onClick: (evt: React.SyntheticEvent) => void;
    thumbnailExperiment?: boolean;
  }
) => {
  const { shouldShowDownloadButton, downloadButtonTelemetryAction } = props;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const videoSubscription = useSelector(selectHasVideoSubscription);
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );

  const isASubscriptionlessUser = isLoggedIn && !videoSubscription;

  if (shouldShowDownloadButton === false) return null;

  const fireDownloadTelemetryActionFromParent = () => {
    downloadButtonTelemetryAction?.();
  };

  const buttonDivClassName = props.thumbnailExperiment
    ? twMerge(
        'text-white bg-gray-900 w-10 h-10 rounded-full p-3 cursor-pointer hover:bg-opacity-90 flex items-center justify-center',
        !showAnimatedAiThumbnailButton && 'bg-opacity-80'
      )
    : 'action-wrapper download-button action-download button-sizing text-white';
  const svgClassName = props.thumbnailExperiment
    ? 'fill-current w-4 h-4'
    : 'action-icon fill-current';

  return (
    <div>
      <Popper
        placement={props.thumbnailExperiment ? Placements.Top : Placements.Left}
        popperClasses="px-2 py-1 text-white bg-black rounded opacity-90 text-sm"
        showContent={showTooltip}
        triggerElement={(referenceElement) => (
          <div
            tabIndex={0}
            className={buttonDivClassName}
            data-cy="download-button"
            ref={referenceElement}
            onClick={props.onClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                props.onClick(e);
                e.preventDefault();
              }
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onFocus={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onBlur={() => setShowTooltip(false)}
            aria-label="download"
            role="button"
          >
            <DownloadIcon
              onClick={fireDownloadTelemetryActionFromParent}
              className={svgClassName}
            />
          </div>
        )}
      >
        <div id="tooltip-download">
          {isASubscriptionlessUser ? 'Download Preview' : 'Download'}
        </div>
      </Popper>
    </div>
  );
};

export const YellowDownloadCTAButton = ({
  disabled = false,
  buttonText = 'Get Unlimited Downloads',
  downloadButtonTelemetryAction,
  onClick,
  ...rest
}) => {
  const fireDownloadTelemetryActionFromParent = () => {
    downloadButtonTelemetryAction?.();
  };

  return (
    <button
      {...rest}
      className="bg-yellow-500 h-full bg-opacity-100 py-2.5 px-3 flex items-center text-white space-x-1.5 rounded-full cursor-pointer border-0 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white"
      onClick={onClick}
    >
      <DownloadIcon
        fill="black"
        className="fill-black h-4 w-4"
        onClick={fireDownloadTelemetryActionFromParent}
      />
      <span className="text-sm leading-5 text-black">{buttonText}</span>
    </button>
  );
};

export const FavoriteButton = (
  props: Pick<
    VideoStockItemCardProps,
    | 'stockItem'
    | 'stockItemFormats'
    | 'favoriteButtonTelemetryAction'
    | 'containPoppers'
    | 'openFoldersInNewWindow'
    | 'context'
  > & { onClick: (e: MouseEvent) => void; thumbnailExperiment?: boolean }
) => {
  const memberBins = useSelector(selectMemberBins);
  const bins = memberBins ? Object.values(memberBins) : [];
  const [showTooltip, setShowTooltip] = React.useState(false);
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );

  const tooltipText = isStockItemInAnyFolder(bins, props.stockItem)
    ? 'Added to Folder'
    : 'Add to Folder';

  const buttonDivClassName = 'action-wrapper button-sizing';
  const svgClassName = props.thumbnailExperiment ? 'w-4 h-4' : 'p-1.5 w-5 h-5';
  const buttonClassName = props.thumbnailExperiment
    ? twMerge(
        'text-white bg-gray-900 w-10 h-10 rounded-full p-3 hover:bg-opacity-90 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white',
        !showAnimatedAiThumbnailButton && 'bg-opacity-80'
      )
    : '';
  const fireFavoriteButtonTelemetryFromParent = () => {
    props.favoriteButtonTelemetryAction?.();
  };

  const originArea = convertStockItemContextToOriginArea(props.context);

  return (
    <div>
      <Popper
        placement={props.thumbnailExperiment ? Placements.Top : Placements.Left}
        popperClasses="px-2 py-1 text-white bg-black rounded opacity-90 text-sm"
        showContent={showTooltip}
        triggerElement={(referenceElement) => (
          <div
            className={buttonDivClassName}
            ref={referenceElement}
            onMouseEnter={() => setShowTooltip(true)}
            onFocus={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onBlur={() => setShowTooltip(false)}
          >
            <FavoriteButtonIcon
              onButtonClick={() => {
                fireFavoriteButtonTelemetryFromParent();
                setShowTooltip(false);
              }}
              onClickAction={props.onClick}
              stockItem={props.stockItem}
              stockItemFormats={props.stockItemFormats}
              telemetryKey="search"
              iconClassName={svgClassName}
              containPoppers={props.containPoppers}
              openFoldersInNewWindow={props.openFoldersInNewWindow}
              originArea={originArea}
              buttonClasses={buttonClassName}
            />
          </div>
        )}
      >
        <div id="tooltip-favorite" className="whitespace-nowrap">
          {tooltipText}
        </div>
      </Popper>
    </div>
  );
};

export const MakerButton = (
  props: Pick<
    VideoStockItemCardProps,
    'stockItem' | 'makerButtonTelemetryAction'
  > & { thumbnailExperiment?: boolean }
) => {
  const buttonDivClassName = props.thumbnailExperiment
    ? 'text-white bg-gray-900 w-10 h-10 rounded-full p-3 bg-opacity-80 hover:bg-opacity-90 cursor-pointer'
    : '';
  const iconClassName = props.thumbnailExperiment
    ? 'fill-current w-4 h-4'
    : 'fill-current text-white w-10 h-10';

  return (
    <SignUpForMakerPopover
      mediaType="video"
      className={buttonDivClassName}
      iconClassName={iconClassName}
      stockItemId={props.stockItem.id}
      onClickAction={() => props.makerButtonTelemetryAction?.()}
      placement={props.thumbnailExperiment ? Placements.Top : Placements.Left}
    />
  );
};

interface RelatedVideosButtonProps {
  onMouseEnter?: () => void;
  onFocus?: () => void;
  onMouseLeave?: () => void;
  onBlur?: () => void;
  onClick?: () => void;
  isSimilarVideosDrawerOpen?: boolean;
}

export const RelatedVideoButton = (props: RelatedVideosButtonProps) => {
  const { isSimilarVideosDrawerOpen, ...rest } = props;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { isCompact, containerRef } = useStockItemCompact();
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );
  return (
    <div ref={containerRef} className="h-full inline-block">
      <Popper
        placement={Placements.Top}
        popperClasses="px-2 py-1 text-white bg-black rounded opacity-90 text-sm"
        showContent={
          showTooltip && (isCompact || showAnimatedAiThumbnailButton)
        }
        triggerElement={(referenceElement) => {
          return (
            <button
              {...rest}
              ref={referenceElement}
              onMouseEnter={() => setShowTooltip(true)}
              onFocus={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onBlur={() => setShowTooltip(false)}
              className={twMerge(
                'bg-gray-900 h-full py-2.5 px-3 flex items-center text-white space-x-1.5 rounded-full cursor-pointer border-0 hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white',
                !showAnimatedAiThumbnailButton && 'bg-opacity-80'
              )}
            >
              <SearchSimilar fill="white" className="fill-white h-4 w-4" />
              {!isCompact && !showAnimatedAiThumbnailButton && (
                <>
                  <span className="text-sm leading-5">Related Video</span>
                  <ArrowIcon
                    isCollapsed={!isSimilarVideosDrawerOpen}
                    invalidSelectionMessage={''}
                    className={'fill-white w-2 h-2'}
                  />
                </>
              )}
            </button>
          );
        }}
      >
        {(isCompact || showAnimatedAiThumbnailButton) && (
          <div className="whitespace-nowrap" id="tooltip-related-search">
            Related Video
          </div>
        )}
      </Popper>
    </div>
  );
};

export const AIEditButton = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { isCompact, containerRef } = useStockItemCompact();
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );
  const defaultButtonClassName = twMerge(
    'bg-gray-900 hover:bg-opacity-90 text-white rounded-full flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white',
    !showAnimatedAiThumbnailButton && 'bg-opacity-80'
  );

  const disabledButtonClassName =
    'bg-gray-200 text-gray-500 rounded-full flex items-center justify-center cursor-not-allowed';

  if (isCompact && !showAnimatedAiThumbnailButton) {
    // Compact version with icon only and tooltip
    return (
      <div ref={containerRef}>
        <Popper
          placement={Placements.Top}
          popperClasses="px-2 py-1 text-white bg-black rounded opacity-90 text-sm"
          showContent={showTooltip}
          triggerElement={(referenceElement) => (
            <div
              className="relative p-0.5 rounded-full"
              style={{
                display: 'inline-block',
                background: `${
                  disabled
                    ? ''
                    : 'linear-gradient(to right, #9952E0, #FAE3D7, #F66E6E)'
                }`,
              }}
              ref={referenceElement}
            >
              <button
                disabled={disabled}
                className={`${
                  disabled ? disabledButtonClassName : defaultButtonClassName
                } w-10 h-10 relative`}
                data-cy="ai-edit-button"
                onClick={onClick}
                onMouseEnter={() => setShowTooltip(true)}
                onFocus={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onBlur={() => setShowTooltip(false)}
                aria-label="Edit with AI"
              >
                <img
                  src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/${
                    disabled ? 'ai-wand-disabled.svg' : 'ai-wand.svg'
                  }`}
                  alt="Edit with AI"
                  className="w-4 h-4"
                />
              </button>
            </div>
          )}
        >
          <div id="tooltip-ai-edit">Edit with AI</div>
        </Popper>
      </div>
    );
  }

  // Full version with text
  return (
    <div ref={containerRef}>
      {showAnimatedAiThumbnailButton && (
        <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
      )}
      <div
        className={twMerge(
          'relative group',
          showAnimatedAiThumbnailButton ? '' : 'p-0.5 rounded-full'
        )}
        style={{
          display: 'inline-block',
          background: `${
            disabled || showAnimatedAiThumbnailButton
              ? ''
              : 'linear-gradient(to right, #9952E0, #FAE3D7, #F66E6E)'
          }`,
        }}
      >
        {showAnimatedAiThumbnailButton && !disabled && (
          <>
            <div
              className="absolute rounded-full overflow-hidden opacity-100 group-hover:opacity-0 transition-opacity duration-300"
              style={{
                top: '-2px',
                right: '-2px',
                bottom: '-2px',
                left: '-2px',
              }}
            >
              <div
                className="absolute"
                style={{
                  width: '200%',
                  height: '200%',
                  left: '-50%',
                  top: '-50%',
                  background:
                    'conic-gradient(transparent 0deg, transparent 270deg, rgb(153, 82, 224) 300deg, rgb(250, 227, 215) 330deg, rgb(255, 102, 230) 345deg, transparent 360deg)',
                  animation: 'spin-slow 3s linear infinite',
                }}
              />
            </div>
            <div
              className="absolute rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                top: '-2px',
                right: '-2px',
                bottom: '-2px',
                left: '-2px',
                background:
                  'linear-gradient(to right, rgb(153, 82, 224), rgb(250, 227, 215), rgb(255, 102, 230))',
              }}
            />
          </>
        )}
        <button
          disabled={disabled}
          className={`${
            disabled ? disabledButtonClassName : defaultButtonClassName
          } px-3 py-2.5 gap-2 w-full relative`}
          data-cy="ai-edit-button"
          onClick={onClick}
          aria-label="Edit with AI"
        >
          <img
            src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/${
              disabled ? 'ai-wand-disabled.svg' : 'ai-wand.svg'
            }`}
            alt="Edit with AI"
            className="w-4 h-4"
          />
          <span className="text-base font-semibold leading-5 text-nowrap">
            Edit with AI
          </span>
        </button>
      </div>
    </div>
  );
};

export const RelatedVideosButtonContainer = (
  props: Pick<VideoStockItemCardProps, 'stockItem'> & {
    onClick: () => void;
  }
) => {
  const drawerIsOpen = useSelector(selectDrawerIsOpen);
  const drawerSelectedItemId = useSelector(selectDrawerSelectedItemId);
  const drawerType = useSelector(selectDrawerType);

  const isSimilarVideosDrawerOpen = () =>
    [
      drawerSelectedItemId === props.stockItem.id,
      drawerType === DrawerType.MORE_LIKE_THIS ||
        drawerType === DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS,
      drawerIsOpen,
    ].every(Boolean);

  return (
    <div>
      <RelatedVideoButton
        onClick={props.onClick}
        isSimilarVideosDrawerOpen={isSimilarVideosDrawerOpen()}
      />
    </div>
  );
};
